/* eslint-disable no-console */
import { WebRTCAdaptor } from '@antmedia/webrtc_adaptor'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useStore } from 'stores'

type ConnectionState = 'idle' | 'initializing' | 'ready' | 'error'
type StreamState = 'idle' | 'loading' | 'playing' | 'stopped' | 'streamNotFound'

type Ctx = {
  connectionState: ConnectionState
  streamState: StreamState
  play: (
    streamId: string,
    videoElement: HTMLVideoElement,
    token?: string
  ) => void
  stop: () => void
  cleanupStream: () => void
  retry: () => void
  disconnect: () => void
}

const AntMediaContext = createContext<Ctx | null>(null)

const DEFAULT_ICE: ReadonlyArray<RTCIceServer> = Object.freeze([
  { urls: 'stun:stun.l.google.com:19302' },
])

const websocketUrl = `wss://${process.env.REACT_APP_ANT_URL}/WebRTCAppEE/websocket`

const logger = {
  log: (...args: any[]) => {
    if (process.env.NODE_ENV === 'production') return
    console.log('[AntMedia]', ...args)
  },
  warn: (...args: any[]) => {
    if (process.env.NODE_ENV === 'production') return
    console.warn('[AntMedia]', ...args)
  },
  error: (...args: any[]) => {
    console.error('[AntMedia]', ...args)
  },
}

export const AntMediaProvider = ({
  children,
  iceServers,
}: {
  children: React.ReactNode
  iceServers?: RTCIceServer[]
}) => {
  const adaptorRef = useRef<WebRTCAdaptor | null>(null)
  const currentVideoElementRef = useRef<HTMLVideoElement | null>(null)
  const currentStreamIdRef = useRef<string | null>(null)

  const iceServersRef = useRef<ReadonlyArray<RTCIceServer>>(DEFAULT_ICE)

  const [connectionState, setConnectionState] =
    useState<ConnectionState>('idle')
  const [streamState, setStreamState] = useState<StreamState>('idle')

  const { loginStore } = useStore()

  useEffect(() => {
    iceServersRef.current =
      iceServers && iceServers.length > 0 ? iceServers : DEFAULT_ICE
  }, [iceServers])

  const attachStreamToVideo = useCallback((stream: MediaStream) => {
    const el = currentVideoElementRef.current
    if (!el) return
    if (el.srcObject !== stream) el.srcObject = stream
    el.muted = true
    el.play?.().catch(() => {})
  }, [])

  const clearVideo = useCallback(() => {
    const el = currentVideoElementRef.current
    if (!el?.srcObject) return
    try {
      ;(el.srcObject as MediaStream)
        ?.getTracks?.()
        .forEach((track) => track.stop?.())
    } catch (error) {
      logger.warn('[AntMedia] Error stopping video tracks:', error)
    }
    el.srcObject = null
  }, [])

  const closeAdaptor = useCallback(() => {
    try {
      const sid = currentStreamIdRef.current
      if (sid && adaptorRef.current) adaptorRef.current.stop(sid)
    } catch (e) {
      logger.warn('[AntMedia] stop on close error:', e)
    }
    try {
      ;(adaptorRef.current as any)?.closeWebSocket?.()
    } catch (error) {
      logger.warn('[AntMedia] Error closing websocket:', error)
    }
    adaptorRef.current = null
    clearVideo()
  }, [clearVideo])

  const initializeAdaptor = useCallback(() => {
    closeAdaptor()

    setConnectionState('initializing')

    const adaptor = new WebRTCAdaptor({
      websocket_url: websocketUrl,
      peerconnection_config: { iceServers: iceServersRef.current },
      remoteVideoId: null,
      localVideoId: null,
      isPlayMode: true,
      sdp_constraints: {
        OfferToReceiveAudio: true,
        OfferToReceiveVideo: true,
        OfferToSendAudio: false,
        OfferToSendVideo: false,
      },
      callback: (info: string, obj: any) => {
        if (info === 'initialized') {
          setConnectionState('ready')
        }

        if (info === 'play_started') {
          setStreamState('playing')
        }

        if (info === 'play_finished') {
          setStreamState('stopped')
        }

        if (info === 'newStreamAvailable' && obj?.stream) {
          attachStreamToVideo(obj.stream as MediaStream)
        }
      },
      callbackError: (err: any, msg: string) => {
        logger.error('[AntMedia SDK] error:', err || msg)

        if (err === 'no_stream_exist') setStreamState('streamNotFound')
        if (err === 'WebSocketNotConnected') {
          setConnectionState('error')
          logger.error('WebSocket not connected')
        }
      },
    })

    adaptorRef.current = adaptor
  }, [attachStreamToVideo, closeAdaptor])

  useEffect(() => {
    initializeAdaptor()
    return () => {
      closeAdaptor()
      setConnectionState('idle')
      setStreamState('idle')
    }
  }, [initializeAdaptor, closeAdaptor])

  const play = useCallback(
    (streamId: string, videoElement: HTMLVideoElement, token = '') => {
      currentVideoElementRef.current = videoElement
      currentStreamIdRef.current = streamId

      const adaptor = adaptorRef.current

      if (!adaptor) return

      setStreamState('loading')

      adaptor.play(streamId, token)
    },
    []
  )

  const stop = useCallback(() => {
    const adaptor = adaptorRef.current
    const sid = currentStreamIdRef.current

    if (!adaptor || !sid) return

    adaptor.stop(sid)

    currentStreamIdRef.current = null
  }, [])

  const cleanupStream = useCallback(() => {
    const adaptor = adaptorRef.current
    const sid = currentStreamIdRef.current

    if (adaptor && sid) adaptor.stop(sid)

    clearVideo()

    currentStreamIdRef.current = null
    currentVideoElementRef.current = null

    setStreamState('idle')
  }, [clearVideo])

  const retry = useCallback(() => {
    initializeAdaptor()
  }, [initializeAdaptor])

  const disconnect = useCallback(() => {
    closeAdaptor()
    currentStreamIdRef.current = null
    currentVideoElementRef.current = null
    setConnectionState('idle')
    setStreamState('idle')
  }, [closeAdaptor])

  useEffect(() => {
    return () => {
      if (!loginStore.isAuthenticated) disconnect()
    }
  }, [loginStore.isAuthenticated, disconnect])

  const value = useMemo<Ctx>(
    () => ({
      connectionState,
      streamState,
      play,
      stop,
      cleanupStream,
      retry,
      disconnect,
    }),
    [connectionState, streamState, play, stop, cleanupStream, retry, disconnect]
  )

  return (
    <AntMediaContext.Provider value={value}>
      {children}
    </AntMediaContext.Provider>
  )
}

export function useAntMedia() {
  const ctx = useContext(AntMediaContext)
  if (!ctx) throw new Error('useAntMedia must be used within AntMediaProvider')
  return ctx
}
